import styled from 'styled-components';

const UserAccessWrapper = styled.div`
  --aa-green: #0b2a1b;
  --aa-green-hover: #0d3521;
  --aa-text: #101828;
  --aa-text-muted: #667085;
  --aa-border: #eaecf0;
  --aa-surface: #ffffff;

  background: #f9fafb;
  min-height: 100%;

  .aa-container {
    width: 100%;
    padding: 16px 24px 40px;
  }

  /* Breadcrumb */
  .aa-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px 0 16px;
    border-bottom: 1px solid var(--aa-border);
    margin-bottom: 24px;

    .aa-breadcrumb-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: var(--aa-text-muted);
      font-size: 14px;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;

      &:hover {
        color: var(--aa-green);
      }
    }

    .aa-breadcrumb-sep {
      color: #d0d5dd;
      font-size: 12px;
    }

    .aa-breadcrumb-current {
      color: var(--aa-text);
      font-weight: 600;
      font-size: 14px;
    }

    .aa-property-chip {
      margin-left: auto;
      padding: 4px 12px;
      border-radius: 16px;
      background: #f2f4f7;
      color: #475467;
      font-size: 13px;
      white-space: nowrap;
    }
  }

  /* Page header */
  .aa-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;

    .aa-title {
      font-size: 26px;
      font-weight: 700;
      color: var(--aa-text);
      margin: 0 0 4px;
    }

    .aa-subtitle {
      color: var(--aa-text-muted);
      font-size: 14px;
      margin: 0;
    }

    .aa-invite-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--aa-green);
      border-color: var(--aa-green);
      height: 42px;
      font-weight: 600;
      border-radius: 8px;
      padding: 0 18px;

      &:hover,
      &:focus {
        background: var(--aa-green-hover);
        border-color: var(--aa-green-hover);
      }
    }
  }

  /* Stat cards */
  .aa-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 28px;

    .aa-stat-card {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 18px 20px;
      border-radius: 12px;
      border: 1px solid var(--aa-border);

      .aa-stat-icon {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        flex-shrink: 0;
      }

      .aa-stat-value {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.1;
        color: var(--aa-text);
      }

      .aa-stat-label {
        font-size: 13px;
        color: var(--aa-text-muted);
      }

      &.green {
        background: #f0fdf4;
        .aa-stat-icon { background: #dcfce7; color: #15803d; }
      }
      &.teal {
        background: #f0fdfa;
        .aa-stat-icon { background: #ccfbf1; color: #0f766e; }
      }
      &.amber {
        background: #fffbeb;
        .aa-stat-icon { background: #fef3c7; color: #b45309; }
      }
      &.blue {
        background: #eff6ff;
        .aa-stat-icon { background: #dbeafe; color: #1d4ed8; }
      }
      &.brand {
        background: var(--aa-green);
        border-color: var(--aa-green);
        .aa-stat-icon { background: rgba(255, 255, 255, 0.18); color: #fff; }
        .aa-stat-value { color: #fff; }
        .aa-stat-label { color: rgba(255, 255, 255, 0.85); }
      }
    }
  }

  /* Tabs */
  .aa-tabs {
    .ant-tabs-nav {
      margin-bottom: 0;
    }
    .ant-tabs-nav::before {
      border-bottom: 1px solid var(--aa-border);
    }
    .ant-tabs-tab {
      font-size: 15px;
      color: var(--aa-text-muted);
      padding: 12px 4px;
    }
    .ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--aa-green) !important;
      font-weight: 600;
    }
    .ant-tabs-ink-bar {
      background: var(--aa-green);
    }
    .ant-tabs-content-holder {
      padding-top: 20px;
    }
    .aa-tab-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      margin-left: 8px;
      border-radius: 10px;
      background: #f2f4f7;
      color: #475467;
      font-size: 12px;
      font-weight: 600;
    }
    .aa-phase-badge {
      margin-left: 8px;
      padding: 1px 8px;
      border-radius: 10px;
      background: #f4f3ff;
      color: #6938ef;
      font-size: 11px;
      font-weight: 600;
    }
  }

  /* Panel below tabs */
  .aa-panel {
    background: var(--aa-surface);
    border: 1px solid var(--aa-border);
    border-top: none;
    border-radius: 0 0 12px 12px;
    padding: 20px 0 0;
  }

  /* Filters row */
  .aa-filters {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 20px 16px;
    flex-wrap: wrap;

    .aa-search {
      flex: 1;
      min-width: 220px;
      max-width: 420px;
      border-radius: 8px;
      border-color: #d0d5dd;
      box-shadow: none;

      /* Keep the neutral default border; suppress the dark-green focus/hover
         ring that comes from the antd primary theme on this input. */
      &:hover,
      &:focus,
      &.ant-input-affix-wrapper-focused {
        border-color: #d0d5dd !important;
        box-shadow: none !important;
      }
      input:focus { outline: none; }
    }
    .aa-filter-icon {
      color: var(--aa-text-muted);
      font-size: 16px;
    }
    .aa-filter-select {
      min-width: 150px;
      .ant-select-selector { border-radius: 8px; }
    }
  }

  /* Suppress browser autofill yellow on any input inside the page. */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
    -webkit-text-fill-color: #101828 !important;
    caret-color: #101828;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* Neutralise the yellow/green hover & focus rings on every input/select
     inside this page. Uses && to double the styled-component class so the
     rule outweighs project-wide theme overrides. */
  && {
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select .ant-select-selector,
    .ant-input-number,
    .ant-picker {
      border-color: #d0d5dd !important;
      box-shadow: none !important;
      outline: none !important;
    }
    /* Hover & mouse-focus: strip the coloured glow but no outline needed
       since the user knows what they just clicked. */
    .ant-input:hover,
    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper:hover,
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused,
    .ant-select,
    .ant-select:hover,
    .ant-select:focus,
    .ant-select-focused,
    .ant-select-open,
    .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
    .ant-select-focused:not(.ant-select-disabled) .ant-select-selector,
    .ant-select-open:not(.ant-select-disabled) .ant-select-selector,
    .ant-select-selection-search-input:focus,
    .ant-picker:hover,
    .ant-picker-focused {
      border-color: #d0d5dd !important;
      box-shadow: none !important;
      outline: none !important;
    }
    /* Keyboard focus: keep a neutral visible outline so users navigating
       by keyboard still get a focus indicator (WCAG 2.4.7 / 2.4.11). */
    .ant-input:focus-visible,
    .ant-input-affix-wrapper:focus-visible,
    .ant-select:focus-visible,
    .ant-select-selector:focus-visible,
    .ant-select-selection-search-input:focus-visible {
      outline: 2px solid #98a2b3 !important;
      outline-offset: 2px;
      box-shadow: none !important;
    }
  }

  /* Users table */
  .aa-table {
    .ant-table {
      font-size: 14px;
    }
    .ant-table-thead > tr > th {
      background: #f9fafb;
      color: #667085;
      font-weight: 600;
      font-size: 12px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      border-bottom: 1px solid var(--aa-border);
    }
    .ant-table-tbody > tr > td {
      border-bottom: 1px solid #f2f4f7;
    }
    .ant-table-tbody > tr:hover > td {
      background: #fafbfc;
    }
  }

  .aa-user-cell {
    display: flex;
    align-items: center;
    gap: 12px;

    .aa-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 600;
      font-size: 14px;
      flex-shrink: 0;
    }
    .aa-user-name {
      font-weight: 600;
      color: var(--aa-text);
      line-height: 1.3;
    }
    .aa-user-email {
      color: var(--aa-text-muted);
      font-size: 13px;
    }
  }

  .aa-role-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .aa-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }
  .aa-badge-custom {
    background: #fffaeb;
    color: #b54708;
    border: 1px solid #fedf89;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .aa-status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 14px;
    color: #344054;

    .aa-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }
    &.active .aa-dot { background: #12b76a; }
    &.pending .aa-dot { background: #f79009; }
    &.deactivated .aa-dot { background: #f04438; }
    &.deactivated { color: #b42318; }
  }

  .aa-muted { color: #98a2b3; }

  .aa-manager-select {
    .ant-select-selector { border-radius: 8px; }
  }

  .aa-actions-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    .aa-manage-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border-radius: 8px;
      border-color: #d0d5dd;
      color: #344054;
      font-weight: 500;

      &:hover {
        border-color: var(--aa-green);
        color: var(--aa-green);
      }
    }
    .aa-kebab {
      color: var(--aa-text-muted);
      border: none;
      box-shadow: none;
      &:hover { color: var(--aa-text); background: #f2f4f7; }
    }
  }

  .aa-empty {
    padding: 48px 20px;
    text-align: center;
    color: var(--aa-text-muted);
  }
  .aa-loading {
    padding: 60px 20px;
    text-align: center;
  }

  /* Roles tab (system roles catalog) */
  .aa-roles {
    padding: 4px 0 20px;

    .aa-roles-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding: 4px 4px 16px;
      flex-wrap: wrap;
    }
    .aa-roles-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--aa-text);
      margin: 0;
    }
    .aa-roles-subtitle {
      color: var(--aa-text-muted);
      font-size: 13px;
      margin: 2px 0 0;
    }
    .aa-roles-head-actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .aa-phase-pill {
      padding: 3px 12px;
      border-radius: 14px;
      background: #f4f3ff;
      color: #6938ef;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }
    .aa-matrix-btn {
      border-radius: 8px;
      border-color: #d0d5dd;
      color: var(--aa-green);
      font-weight: 500;
      &:hover { border-color: var(--aa-green); }
    }

    .aa-role-row-wrap {
      border-top: 1px solid #f2f4f7;
    }

    .aa-role-row {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 8px;

      .aa-role-icon {
        width: 38px;
        height: 38px;
        border-radius: 9px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 17px;
        flex-shrink: 0;
      }
      .aa-role-main { flex: 1; min-width: 0; }
      .aa-role-name {
        font-weight: 600;
        color: var(--aa-text);
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .aa-system-tag {
        padding: 1px 8px;
        border-radius: 6px;
        background: #f2f4f7;
        color: #475467;
        font-size: 11px;
        font-weight: 500;
      }
      .aa-role-desc {
        color: var(--aa-text-muted);
        font-size: 13px;
        margin-top: 2px;
      }
      .aa-role-count {
        text-align: right;
        min-width: 56px;
        .n { font-weight: 700; color: var(--aa-text); display: block; }
        .u { font-size: 12px; color: var(--aa-text-muted); }
      }
      .aa-role-toggle {
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 8px;
        color: #475467;
        &:hover { background: #f2f4f7; color: var(--aa-text); }
        &:disabled,
        &:disabled:hover { background: transparent; color: #d0d5dd; }
      }
      .aa-clone-btn {
        border-radius: 8px;
        border-color: #d0d5dd;
      }
    }

    .aa-role-users {
      padding: 8px 16px 16px 60px;
      background: #f9fafb;
      border-top: 1px dashed #eaecf0;
    }
    .aa-role-user {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      .aa-avatar {
        width: 32px; height: 32px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        color: #fff; font-weight: 600; font-size: 12px; flex-shrink: 0;
      }
      .aa-role-user-meta { flex: 1; min-width: 0; }
      .aa-user-name { font-weight: 600; color: var(--aa-text); line-height: 1.3; }
      .aa-user-email { color: var(--aa-text-muted); font-size: 13px; }
    }
    .aa-role-users-empty {
      padding: 8px 0;
      color: var(--aa-text-muted);
      font-size: 13px;
    }
  }

  /* Settings tab */
  .aa-settings {
    padding: 0 20px 20px;
    max-width: 640px;

    .settings-radio-group .ant-radio-wrapper {
      display: block;
      margin: 8px 0;
    }
    .settings-suboptions {
      margin: 10px 0 4px 24px;
      padding: 12px 16px;
      background: #f9fafb;
      border: 1px solid var(--aa-border);
      border-radius: 8px;
    }
    .settings-actions {
      margin-top: 18px;
    }
  }

  .aa-primary-btn {
    background: var(--aa-green);
    border-color: var(--aa-green);
    border-radius: 8px;
    font-weight: 600;
    &:hover, &:focus {
      background: var(--aa-green-hover);
      border-color: var(--aa-green-hover);
    }
  }

  @media only screen and (max-width: 900px) {
    .aa-stats { grid-template-columns: repeat(2, 1fr); }
  }
  @media only screen and (max-width: 600px) {
    .aa-stats { grid-template-columns: 1fr; }
    .aa-page-header { flex-direction: column; }
    .aa-container { padding: 12px 14px 32px; }
  }
`;

export default UserAccessWrapper;

// Rendered inside antd Modals, which portal outside UserAccessWrapper, so these
// styles live in their own styled component.
export const ModalContent = styled.div`
  /* Suppress browser autofill yellow on inputs inside modals (Modal portals
     out of UserAccessWrapper, so its rule does not reach them). */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
    -webkit-text-fill-color: #101828 !important;
    caret-color: #101828;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* Same hard neutralisation as the page wrapper for modal inputs/selects. */
  && {
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select .ant-select-selector {
      border-color: #d0d5dd !important;
      box-shadow: none !important;
      outline: none !important;
    }
    /* Hover & mouse-focus: strip the coloured glow but no outline. */
    .ant-input:hover,
    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper:hover,
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused,
    .ant-select,
    .ant-select:hover,
    .ant-select-focused,
    .ant-select-open,
    .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
    .ant-select-focused:not(.ant-select-disabled) .ant-select-selector,
    .ant-select-open:not(.ant-select-disabled) .ant-select-selector,
    .ant-select-selection-search-input:focus {
      border-color: #d0d5dd !important;
      box-shadow: none !important;
      outline: none !important;
    }
    /* Keyboard focus: keep a neutral visible outline (WCAG 2.4.7 / 2.4.11). */
    .ant-input:focus-visible,
    .ant-input-affix-wrapper:focus-visible,
    .ant-select:focus-visible,
    .ant-select-selector:focus-visible,
    .ant-select-selection-search-input:focus-visible {
      outline: 2px solid #98a2b3 !important;
      outline-offset: 2px;
      box-shadow: none !important;
    }
  }

  .ma-userhead {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #0b2a1b;
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 20px;

    .ma-avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 600;
      background: rgba(255, 255, 255, 0.18);
      flex-shrink: 0;
    }
    .ma-name { color: #fff; font-weight: 600; font-size: 16px; }
    .ma-email { color: rgba(255, 255, 255, 0.75); font-size: 13px; }
    .ma-status {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding: 1px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
      .dot { width: 7px; height: 7px; border-radius: 50%; }
      &.active .dot { background: #32d583; }
      &.pending .dot { background: #fdb022; }
      &.deactivated .dot { background: #f97066; }
    }
  }

  .ma-section-label {
    font-weight: 600;
    color: #101828;
    margin: 18px 0 8px;
    font-size: 14px;
  }
  .ma-role-desc-box {
    border: 1px solid #eaecf0;
    background: #f9fafb;
    border-radius: 8px;
    padding: 10px 14px;
    color: #475467;
    font-size: 13px;
    margin-top: 8px;
  }
  .ma-hint { color: #98a2b3; font-size: 12px; margin-top: 4px; }
  /* A value that is set and cannot be changed here — reads as a field, not as a
     disabled input the user might keep trying to click. */
  .ma-readonly {
    border: 1px solid #eaecf0; border-radius: 8px; padding: 7px 11px;
    background: #f9fafb; color: #344054; font-size: 14px;
  }

  .ma-safety-list {
    border: 1px solid #eaecf0;
    border-radius: 8px;
    overflow: hidden;
  }
  .ma-safety-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 14px;
    border-bottom: 1px solid #f2f4f7;
    &:last-child { border-bottom: none; }
    .ma-safety-label {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #344054;
      .anticon { color: #0b2a1b; }
    }
  }

  /* Reference permission matrix */
  .pm-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 14px;
    font-size: 12px;
    color: #667085;
    .pm-legend-item { display: inline-flex; align-items: center; gap: 6px; }
    .pm-swatch { width: 12px; height: 12px; border-radius: 4px; display: inline-block; }
  }
  .pm-table-wrap { overflow-x: auto; }
  .pm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      padding: 8px 10px;
      text-align: center;
      border-bottom: 1px solid #f2f4f7;
    }
    thead th {
      background: #0b2a1b;
      color: #fff;
      font-weight: 600;
      position: sticky;
      top: 0;
    }
    thead th:first-child, td.pm-module { text-align: left; }
    td.pm-module { color: #344054; }
    tr.pm-group td {
      background: #f9fafb;
      font-weight: 600;
      color: #667085;
      text-transform: uppercase;
      font-size: 11px;
      letter-spacing: 0.04em;
      text-align: left;
    }
    .pm-cap {
      display: inline-block;
      min-width: 48px;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }
  }
`;
